import IssueForm from './components/IssueForm';
import ParserPopup from './components/ParserPopup';
import './index.css';
import { printData } from './utils/printing';
import {
  getSelectedProvider,
  providerNames,
  setSelectedProvider,
} from './utils/storage';
import {
  $items,
  $showPageNumbers,
  loadDeserializedData,
  resetData,
  serializeData,
} from './utils/store';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import { Icon } from '@iconify/react';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import { useCallback } from 'react';

export const $popup = atom<React.ReactNode>(null);

export function App() {
  const popup = useStore($popup);

  const onPrint = useCallback(async () => {
    const pdf = printData(serializeData());

    pdf.open();
  }, []);

  const onSave = useCallback(async () => {
    const data = serializeData();
    await getSelectedProvider().saveData(data);
  }, []);

  const onSaveAs = useCallback(async () => {
    const data = serializeData();
    await getSelectedProvider().saveAs(data);
  }, []);

  const onLoad = useCallback(async () => {
    const data = await getSelectedProvider().loadData();
    if (data) {
      loadDeserializedData(data);
    }
  }, []);

  const onReset = useCallback(() => {
    if (!window.confirm('Are you sure you want to reset?')) return;
    resetData();
  }, []);

  const onPasteParse = useCallback(async () => {
    const clipboard = await navigator.clipboard.readText();

    const parserPopup = (
      <ParserPopup
        input={clipboard}
        onParse={items => {
          $items.set(items);
          $popup.set(null);
        }}
        onCancel={() => $popup.set(null)}
      />
    );

    $popup.set(parserPopup);
  }, []);

  const showPageNumbers = useStore($showPageNumbers);

  return (
    <div className="not-print:p-4">
      <div className="flex flex-wrap items-center justify-center print:hidden">
        {popup}

        <button
          className="m-2 flex cursor-pointer items-center gap-2 rounded bg-gray-200 pr-4 pl-2 hover:bg-gray-300"
          onClick={onPrint}
        >
          <Icon icon="selfhst:adobe-acrobat" className="h-6 w-6" />
          <span className="py-2">Print to PDF</span>
        </button>
        <div className="m-2 flex">
          <button
            className="flex cursor-pointer gap-2 rounded-l bg-blue-200 px-2 py-2 hover:bg-blue-300"
            onClick={onSave}
          >
            <Icon icon="mdi:content-save" className="h-6 w-6" />
            Save
          </button>
          <Menu>
            <MenuButton className="cursor-pointer rounded-r bg-blue-200 px-1 py-2 hover:bg-blue-300">
              <Icon icon="mdi:chevron-down" />
            </MenuButton>
            <MenuItems anchor="bottom" className="flex flex-col">
              {providerNames.map(provider => (
                <button
                  key={provider}
                  className="cursor-pointer rounded bg-gray-200 px-4 py-2 hover:bg-gray-300"
                  onClick={() => {
                    setSelectedProvider(provider);
                    onSaveAs();
                  }}
                >
                  Save as {provider}
                </button>
              ))}
            </MenuItems>
          </Menu>
        </div>
        <div className="m-2 flex">
          <button
            className="flex cursor-pointer gap-2 rounded-l bg-blue-200 px-2 py-2 hover:bg-blue-300"
            onClick={onLoad}
          >
            <Icon icon="mdi:folder" className="h-6 w-6" />
            Load
          </button>
          <Menu>
            <MenuButton className="cursor-pointer rounded-r bg-blue-200 px-1 py-2 hover:bg-blue-300">
              <Icon icon="mdi:chevron-down" />
            </MenuButton>
            <MenuItems anchor="bottom" className="flex flex-col">
              {providerNames.map(provider => (
                <button
                  key={provider}
                  className="cursor-pointer rounded bg-gray-200 px-4 py-2 hover:bg-gray-300"
                  onClick={() => {
                    setSelectedProvider(provider);
                    onLoad();
                  }}
                >
                  Load from {provider}
                </button>
              ))}
            </MenuItems>
          </Menu>
        </div>
        <button
          className="flex cursor-pointer gap-2 rounded bg-blue-200 px-2 py-2 pr-4 hover:bg-blue-300"
          onClick={onPasteParse}
        >
          <Icon icon="mdi:content-paste" className="h-6 w-6" />
          Paste
        </button>
        <label className="m-2 flex items-center gap-2">
          <input
            type="checkbox"
            checked={showPageNumbers}
            onChange={e => $showPageNumbers.set(e.target.checked)}
          />
          Show page numbers
        </label>

        <button
          className="ml-auto flex cursor-pointer gap-2 rounded bg-red-200 px-2 py-2 pr-4 hover:bg-red-300"
          onClick={onReset}
        >
          <Icon icon="mdi:delete" className="h-6 w-6" />
          Reset
        </button>
      </div>
      <IssueForm />
    </div>
  );
}

export default App;
